/**
 * bundle-section.js
 * Reads ticked product cards in a "bundle" section (with per-card variant
 * pickers) and adds them all to the cart in one request using the
 * Storefront GraphQL API's cartLinesAdd mutation (creating a cart first
 * if one doesn't exist yet).
 *
 * Requires a <script data-bundle-config="{{ section.id }}"> JSON block
 * (see bundle-section.liquid) with: shopDomain, storefrontAccessToken,
 * apiVersion, cartRedirect, currencyCode.
 */

(function () {
  const CART_LINES_ADD_MUTATION = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          totalQuantity
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
        warnings {
          code
          message
        }
      }
    }
  `;

  class BundleSection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.id.replace('BundleSection-', '');

      const configEl = document.querySelector(
        `script[data-bundle-config="${this.sectionId}"]`
      );
      this.config = configEl ? JSON.parse(configEl.textContent) : {};

      this.cards = Array.from(root.querySelectorAll('.bundle-card'));
      this.button = root.querySelector('[data-bundle-add-to-cart]');
      this.summaryEl = root.querySelector('[data-bundle-summary]');

      this.cards.forEach((card) => {
        const checkbox = card.querySelector('[data-bundle-item]');
        const select = card.querySelector('[data-bundle-variant-select]');

        if (checkbox) {
          checkbox.addEventListener('change', () => this.updateSummary());
        }

        if (select) {
          select.addEventListener('change', () => this.handleVariantChange(card));
        }
      });

      if (this.button) {
        this.button.addEventListener('click', () => this.handleAddToCart());
      }

      this.updateSummary();
    }

    handleVariantChange(card) {
      const select = card.querySelector('[data-bundle-variant-select]');
      const checkbox = card.querySelector('[data-bundle-item]');
      const priceEl = card.querySelector('[data-bundle-price]');
      const selectedOption = select.options[select.selectedIndex];

      const variantId = selectedOption.value;
      const price = parseFloat(selectedOption.dataset.price || '0');
      const available = selectedOption.dataset.available === 'true';

      checkbox.dataset.variantId = variantId;
      checkbox.dataset.price = price;
      checkbox.dataset.available = available;
      checkbox.disabled = !available;
      if (!available) checkbox.checked = false;

      if (priceEl) {
        priceEl.innerHTML = available
          ? `<span class="bundle-card__price--current">RM ${(price / 100).toFixed(2)}</span>`
          : `<span class="bundle-card__price--unavailable">Sold out</span>`;
      }

      this.updateSummary();
    }

    getGraphQLEndpoint() {
      const { shopDomain, apiVersion } = this.config;
      return `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    }

    async graphqlRequest(query, variables) {
      const response = await fetch(this.getGraphQLEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        throw new Error(`Storefront API request failed: ${response.status}`);
      }

      const json = await response.json();

      if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join(', '));
      }

      return json.data;
    }

    getCheckedCheckboxes() {
      return this.cards
        .map((card) => card.querySelector('[data-bundle-item]'))
        .filter(
          (checkbox) =>
            checkbox &&
            checkbox.checked &&
            !checkbox.disabled &&
            checkbox.dataset.variantId
        );
    }

    getSelectedLines() {
      return this.getCheckedCheckboxes().map((checkbox) => ({
        merchandiseId: checkbox.dataset.variantId,
        quantity: 1,
      }));
    }

    updateSummary() {
      const checked = this.getCheckedCheckboxes();
      const count = checked.length;
      const total = checked.reduce(
        (sum, checkbox) => sum + parseFloat(checkbox.dataset.price || '0'),
        0
      );

      if (this.button) {
        this.button.disabled = count === 0;
      }

      if (this.summaryEl) {
        this.summaryEl.textContent =
          count === 0
            ? 'RM 0.00'
            : `Total: RM ${(total / 100).toFixed(2)}`;
      }
    }

    async getCartId() {
      // Every visitor already has a cart tied to their session (the same
      // one the rest of the theme uses). Read its token via the Ajax Cart
      // API and convert it to a Storefront GID — no separate cart to create
      // or track ourselves.
      const response = await fetch('/cart.js');

      if (!response.ok) {
        throw new Error(`Could not read the current cart: ${response.status}`);
      }

      const cart = await response.json();
      return `gid://shopify/Cart/${cart.token}`;
    }

    pushDataLayerEvent(checkboxes) {
      window.dataLayer = window.dataLayer || [];

      const items = checkboxes.map((checkbox) => ({
        item_id: `shopify_MY_${checkbox.dataset.productId}_${checkbox.dataset.variantId.replace('gid://shopify/ProductVariant/','')}` || '',
        item_name: checkbox.dataset.productName || '',
        price: parseFloat((checkbox.dataset.price/100).toFixed(2) || '0'),
        quantity: 1,
      }));

      console.log(items);

      window.dataLayer.push({
        event: 'add_to_cart_button',
        ecommerce: {
          items,
        },
      });
    }

    async handleAddToCart() {
      const checkedCheckboxes = this.getCheckedCheckboxes();
      const lines = this.getSelectedLines();

      if (!lines.length) {
        console.warn('Bundle: select at least one product first.');
        return;
      }

      if (!this.config.storefrontAccessToken) {
        console.error('Bundle: missing Storefront API access token.');
        return;
      }

      this.pushDataLayerEvent(checkedCheckboxes);

      this.button.disabled = true;

      try {
        const cartId = await this.getCartId();
        
        const addData = await this.graphqlRequest(CART_LINES_ADD_MUTATION, {
          cartId,
          lines,
        });
        console.log(cartId,lines)
        
        const { cart, userErrors, warnings } = addData.cartLinesAdd;

        if (userErrors && userErrors.length) {
          throw new Error(userErrors.map((e) => e.message).join(', '));
        }

        if (warnings && warnings.length) {
          console.warn('cartLinesAdd warnings:', warnings);
        }

        document.dispatchEvent(
          new CustomEvent('bundle:cart-updated', { detail: { cart } })
        );

        if (this.config.cartRedirect && cart && cart.checkoutUrl) {
          window.location.href = cart.checkoutUrl.replace(/\/checkouts\/.*$/, '/cart');
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.updateSummary();
      }
    }
  }

  function initBundleSections() {
    document
      .querySelectorAll('.bundle-section')
      .forEach((root) => new BundleSection(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleSections);
  } else {
    initBundleSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('.bundle-section');
    if (root) new BundleSection(root);
  });
})();